/**
 * Typography variants – single source of truth for font family, size, weight, and spacing.
 * Uses Tailwind classes; fonts are font-bebas-neue (display/headings) and font-poppins (body/labels).
 */

export const typography = {
  /** Large display – hero, main page titles */
  display:
    'font-arabic  font-normal uppercase leading-[1.1] tracking-[0.05em] text-4xl sm:text-5xl lg:text-[61.6px] lg:leading-[72px]',

  /** Section heading – Arabic 700, 49.54px, 60px line-height, 0.5px letter-spacing */
  sectionHeading: 'font-arabic font-bold uppercase text-[49.54px] leading-[60px] tracking-[0.5px]',

  /** Page/section h1 */
  h1: 'font-arabic font-normal leading-[1.2] text-2xl text-[#1a1a1a] sm:text-3xl lg:text-4xl',

  /** Section h2 – e.g. MISSION, ABOUT GALLIUM FILTERS */
  h2: 'font-arabic font-normal uppercase leading-[1.2] tracking-[0.03em] text-2xl sm:text-3xl lg:text-4xl',

  /** Subsection h2 – accent color */
  h2Accent:
    'font-arabic font-normal uppercase leading-[1.2] tracking-[0.03em] text-2xl text-[#FF0000] sm:text-3xl lg:text-4xl',

  /** Orange mission-style h2 */
  h2Mission:
    'font-arabic font-normal uppercase leading-[1.2] tracking-[0.03em] text-3xl text-[#FF6D2C] sm:text-4xl lg:text-5xl',

  /** Card/timeline h3 */
  h3: 'font-arabic text-base font-bold uppercase tracking-normal text-[#1a1a1a] md:text-lg lg:text-xl',

  /** Small heading – timeline, list titles */
  h3Small:
    'font-arabic text-lg font-normal uppercase leading-[1.2] text-[#1a1a1a] sm:text-xl lg:text-2xl',

  /** Large number/stat – e.g. 5000+ */
  stat: 'font-arabic text-7xl font-normal uppercase leading-[1.1] sm:text-8xl lg:text-9xl',

  /** Stat label line below number */
  statLabel: 'font-arabic mt-1 text-xl font-normal uppercase leading-[1.3] text-[#111] sm:text-2xl',

  /** Section label – small uppercase with diamond/prefix */
  sectionLabel: 'font-arabic text-xs font-semibold uppercase tracking-[0.05em] text-[#FF0000]',

  /** Neutral section label */
  sectionLabelMuted:
    'font-arabic text-sm font-semibold uppercase tracking-[0.05em] text-neutral-600',

  /** Overline / category */
  overline: 'font-arabic text-xs font-medium uppercase tracking-[0.05em]',

  /** Designation / role */
  designation: 'font-arabic text-xs font-medium uppercase tracking-[0.05em] text-[#FF0000]',

  /** Body – default paragraph */
  body: 'font-arabic text-sm font-light leading-[1.8] text-neutral-700',

  /** Body larger – hero message */
  bodyLg: 'font-arabic text-[17px] leading-[1.8] text-neutral-700',

  /** Body small – captions, timeline description */
  bodySm: 'font-arabic text-xs leading-[1.8] text-neutral-700 sm:text-sm',

  /** Mission/black background body */
  bodyInverse: 'font-arabic text-[15.61px] font-light leading-[1.8] text-white',

  /** Card description */
  caption: 'font-arabic text-sm leading-[1.7] text-neutral-600 md:text-base',

  /** CTA button text */
  cta: 'font-arabic font-semibold uppercase text-xs tracking-[0.04em]',

  /** Link / nav */
  nav: 'font-arabic text-[15px] font-normal leading-[1.6] tracking-[0.03em] uppercase',
} as const

export type TypographyVariant = keyof typeof typography
